import { useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Color, HR_EMPLOYEE_ROUTE, HR_EMPLOYEES_ROUTE, MY_INFO_PATH, MY_INFO_ROUTE, Size, employeeFilePath, formatDateOnly, pageKindLineText, t, unemploymentClaimsRoute,
  type DateFormat, type EmployeeFile, type EmployeeFileRow, type EmployeeFileSectionKey, type MyInfoAnswer, type StringKey,
} from '@bms2/shared';
import { AsyncStatePanel } from '../../components/composites/AsyncStatePanel';
import { Tile, type TileItem } from '../../components/composites/Tile';
import { Button } from '../../components/controls';
import { PageModule } from '../../shell/PageModule';
import { useDateFormat, useViewerZone } from '../../shell/ViewerZone';
import { whenText } from '../../shell/viewerDates';
import { useApi } from '../records/api';
import { ClockCredentials } from './ClockCredentials';
import { EmergencyContactsKeeper, EmergencyReasonRead } from './EmergencyContacts';
import { EditEmployeeWindow, EditHomeAddressWindow } from './HireWindow';
import { PayControls } from './PayChange';
import { ReviewFiles } from './ReviewFiles';
import { TimeCorrections } from './TimeCorrections';
import { WorkingPatternControls } from './WorkingPattern';
import { TimeOffEnrollmentControls } from './TimeOffEnrollment';
import { TimeOffControls } from './RequestTimeOff';
import { TimeOffBegunControls } from './TimeOffBegun';
import { HrPagesNav } from './HrPagesNav';
import styles from './HrPages.module.css';

export { HR_EMPLOYEE_ROUTE, MY_INFO_ROUTE };

/** A section reads as wide as the page allows, never narrower than a tile. */
const SECTION_MIN_WIDTH = 260;

/** Each section's heading. */
const SECTION_TITLES: Readonly<Record<EmployeeFileSectionKey, StringKey>> = {
  job: 'hr.file.section.job',
  pay: 'hr.file.section.pay',
  place: 'hr.file.section.place',
  access: 'hr.file.section.access',
  time: 'hr.file.section.time',
  trainings: 'hr.file.section.trainings',
  custody: 'hr.file.section.custody',
  emergency: 'hr.file.section.emergency',
  reviews: 'hr.file.section.reviews',
  attendance: 'hr.file.section.attendance',
  timeOff: 'hr.file.section.timeOff',
};

/** A row's date: a calendar day in the reader's format, an instant in their zone and format. */
function dateText(at: string, zone: string, format: DateFormat): string {
  return /^\d{4}-\d{2}-\d{2}$/.test(at) ? formatDateOnly(`${at}T00:00:00.000Z`, format) : whenText(at, zone, format);
}

/** One row as the Tile's full-width row draws it: the label, the value at the right, the dates stacked, the caption, the pill. */
export function employeeFileItem(row: EmployeeFileRow, zone: string, format: DateFormat): TileItem {
  return {
    type: 'fullWidthRow',
    text: row.detail ? `${row.label} · ${row.detail}` : row.label,
    value: row.value ?? null,
    heading: row.heading ?? null,
    when: row.at ? { from: dateText(row.at, zone, format), until: row.until ? dateText(row.until, zone, format) : null } : null,
    route: row.route ?? null,
    pill: row.pill ?? null,
  };
}

/** The sections of one file, each a tile; an empty one says None yet in the tile's own quiet line. HR's controls draw under their section. */
function FileSections({ file, under = {} }: { file: EmployeeFile; under?: Partial<Record<EmployeeFileSectionKey, ReactNode>> }) {
  const zone = useViewerZone();
  const format = useDateFormat();
  return (
    <div className={styles.fileSections} data-employee-file={file.employmentId} data-employee-reader={file.reader}>
      {file.sections.map((section) => (
        <div key={section.key} data-employee-section={section.key}>
          <Tile
            title={t(SECTION_TITLES[section.key])}
            items={section.rows.map((row) => employeeFileItem(row, zone, format))}
            minWidth={SECTION_MIN_WIDTH}
            maxWidth={Infinity}
          />
          {under[section.key] ?? null}
        </div>
      ))}
    </div>
  );
}

/** The sentence over the sections: a manager reads read-only, and an ended job reads as it stood. */
function FileNotice({ file }: { file: EmployeeFile }) {
  const notices = [file.former ? t('hr.file.former') : null, file.reader === 'chain' ? t('hr.file.readOnlyChain') : null].filter((line): line is string => line !== null);
  if (notices.length === 0) return null;
  return (
    <div data-employee-notice={file.former ? 'former' : 'chain'}>
      {notices.map((line) => <p key={line} className={styles.meta}>{line}</p>)}
    </div>
  );
}

/**
 * One job's Employee page (piece 34, *The employee file*; decision 3180):
 * HR › Employees › the person. 🔒 The route answers a holder of the HR pages
 * permission and a manager above the job in its chain, read-only, and refuses
 * anybody else in the sentence the panel draws. Only the sections this reader
 * reads arrive, so the page draws what it is sent.
 */
export function EmployeePage({ apiBaseUrl = '' }: { apiBaseUrl?: string }) {
  const { employmentId = '' } = useParams();
  const fetched = useApi<EmployeeFile>(employmentId ? employeeFilePath(employmentId) : null, apiBaseUrl);
  const [editing, setEditing] = useState(false);
  const [editingHome, setEditingHome] = useState(false);
  const title = fetched.state.kind === 'ready' ? fetched.state.data.name : t('hr.employees.title');
  // 🔒 Edit Employee draws where the file sent its values: a holder of the hire permission, on a job that has not ended (decision 3181).
  const edit = fetched.state.kind === 'ready' ? (fetched.state.data.edit ?? null) : null;
  // 🔒 Edit Home Address draws on the same terms, with the private address's values sent to HR alone (decision 3182).
  const home = fetched.state.kind === 'ready' ? (fetched.state.data.home ?? null) : null;
  const navigate = useNavigate();
  // 🔒 Decision 10651: the job's unemployment claims, offered where the file says the viewer holds the hire permission as HR, which the claim routes ask.
  const readsAsHr = fetched.state.kind === 'ready' && fetched.state.data.mayRecordClaims === true;
  const actions = [
    readsAsHr ? <span key="claims" data-employee-claims="open"><Button size={Size.Small} color={Color.Inactive} text={[t('hr.claims.open')]} actions={[() => navigate(unemploymentClaimsRoute(employmentId))]} /></span> : null,
    home ? <span key="home" data-employee-home="open"><Button size={Size.Small} color={Color.Inactive} text={[t('hr.home.button')]} actions={[() => setEditingHome(true)]} /></span> : null,
    edit ? <span key="edit" data-employee-edit="open"><Button size={Size.Small} color={Color.Navy} text={[t('hr.edit.button')]} actions={[() => setEditing(true)]} /></span> : null,
  ].filter((action) => action !== null);
  return (
    <PageModule
      title={title}
      subtitle={pageKindLineText({ page: 'named', route: HR_EMPLOYEE_ROUTE })}
      icon="hr"
      newAction={actions.length > 0 ? <span className={styles.controls}>{actions}</span> : null}
    >
      <div className={styles.page} data-page="hr-employee">
        <HrPagesNav current={HR_EMPLOYEES_ROUTE} />
        <AsyncStatePanel state={fetched.state} emptyAction={t('hr.file.notFound')} onRetry={fetched.reload}>
          {(file) => (
            <>
              <FileNotice file={file} />
              <FileSections
                file={file}
                under={{
                  // 🔒 Edit Working Pattern under the Job section, for HR with the hire permission and a manager above an open job (decision 12850).
                  // 🔒 Decision 13130: Enroll in a Plan and End beside it, for HR reading as HR with the hire permission alone.
                  ...(file.workingPattern || file.timeOffEnrollment
                    ? {
                        job: (
                          <>
                            {file.workingPattern ? <WorkingPatternControls apiBaseUrl={apiBaseUrl} employmentId={file.employmentId} section={file.workingPattern} onSaved={fetched.reload} /> : null}
                            {file.timeOffEnrollment ? <TimeOffEnrollmentControls apiBaseUrl={apiBaseUrl} employmentId={file.employmentId} section={file.timeOffEnrollment} onSaved={fetched.reload} /> : null}
                          </>
                        ),
                      }
                    : {}),
                  // 🔒 Decision 13251: Cancel Remaining Days on an approved request under way, for HR reading as HR with the hire permission.
                  ...(file.timeOffBegun ? { timeOff: <TimeOffBegunControls apiBaseUrl={apiBaseUrl} section={file.timeOffBegun} onCanceled={fetched.reload} /> } : {}),
                  // 🔒 Change Pay and the waiting change's decision, to a holder of the hire permission reading as HR (decision 3781).
                  ...(file.pay ? { pay: <PayControls apiBaseUrl={apiBaseUrl} employmentId={file.employmentId} pay={file.pay} onChanged={fetched.reload} /> } : {}),
                  ...(file.credentials ? { access: <ClockCredentials apiBaseUrl={apiBaseUrl} employmentId={file.employmentId} credentials={file.credentials} onChanged={fetched.reload} /> } : {}),
                  // 🔒 A manager reads the contacts only through the reason form (decision 3184).
                  ...(file.emergency?.behindReason ? { emergency: <EmergencyReasonRead apiBaseUrl={apiBaseUrl} employmentId={file.employmentId} /> } : {}),
                  // 🔒 HR opens the signed reviews, and HR with the hire permission or a manager above the job files one (decision 3185).
                  // 🔒 Decision 12930: the corrections to the job's time; the employee answers them, the chain above the job reads.
                  ...(file.timeCorrections ? { time: <TimeCorrections apiBaseUrl={apiBaseUrl} employmentId={file.employmentId} section={file.timeCorrections} own={file.reader === 'self'} onAnswered={fetched.reload} /> } : {}),
                  ...(file.reviews ? { reviews: <ReviewFiles apiBaseUrl={apiBaseUrl} employmentId={file.employmentId} rows={file.sections.find((section) => section.key === 'reviews')?.rows ?? []} reviews={file.reviews} onFiled={fetched.reload} /> } : {}),
                }}
              />
            </>
          )}
        </AsyncStatePanel>
      </div>
      {/* 🔨 Mounted only while open and keyed to the job, so a reopening reads the position in force afresh. */}
      {editing && edit ? (
        <EditEmployeeWindow
          key={employmentId}
          apiBaseUrl={apiBaseUrl}
          employmentId={employmentId}
          start={edit}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            fetched.reload();
          }}
        />
      ) : null}
      {editingHome && home ? (
        <EditHomeAddressWindow
          key={`home-${employmentId}`}
          apiBaseUrl={apiBaseUrl}
          employmentId={employmentId}
          start={home}
          onClose={() => setEditingHome(false)}
          onSaved={() => {
            setEditingHome(false);
            fetched.reload();
          }}
        />
      ) : null}
    </PageModule>
  );
}

/**
 * My Info (decision 3180): the signed-in person's own employee file, opened from
 * the user menu beside Account Settings. 🔒 It reads the person's current job at
 * our own Company as the employee, read-only except where a section names the
 * employee as its keeper; a person with no such job is told so.
 */
export function MyInfoPage({ apiBaseUrl = '' }: { apiBaseUrl?: string }) {
  const fetched = useApi<MyInfoAnswer>(MY_INFO_PATH, apiBaseUrl);
  return (
    <PageModule title={t('myInfo.title')} subtitle={pageKindLineText({ page: 'named', route: MY_INFO_ROUTE })} icon="hr">
      <div className={styles.page} data-page="my-info">
        <AsyncStatePanel state={fetched.state} emptyAction={t('myInfo.none')} onRetry={fetched.reload}>
          {(answer) => answer.file === null
            ? <p className={styles.meta} data-my-info="none">{t('myInfo.none')}</p>
            : (
              <>
                <FileNotice file={answer.file} />
                <FileSections
                  file={answer.file}
                  under={{
                    ...(answer.file.emergency?.contacts ? { emergency: <EmergencyContactsKeeper apiBaseUrl={apiBaseUrl} contacts={answer.file.emergency.contacts} onSaved={fetched.reload} /> } : {}),
                    // 🔒 Decision 12930: the employee answers a manager's correction of their time, Agree or Dispute, once.
                    // 🔒 Decision 13030: Request Time Off and Cancel Request, the person's own acts on their own balances.
                    timeOff: <TimeOffControls apiBaseUrl={apiBaseUrl} onChanged={fetched.reload} />,
                    ...(answer.file.timeCorrections ? { time: <TimeCorrections apiBaseUrl={apiBaseUrl} employmentId={answer.file.employmentId} section={answer.file.timeCorrections} own onAnswered={fetched.reload} /> } : {}),
                  }}
                />
              </>
            )}
        </AsyncStatePanel>
      </div>
    </PageModule>
  );
}
